"use client";

import { useState } from "react";
import type { AgenticCartBundleSummaryResponse } from "@ishvaram/api-client";
import type { ServiceabilityInfo } from "@/lib/api/serviceability";
import type {
  CartRecoveryState,
  CartStatus,
  ContactForm,
  ShippingForm,
} from "./AgenticCartCheckout.types";
import { PlanSummary } from "./AgenticCartCheckout.summary";
import { WhatsappOptInCheckbox } from "@/components/WhatsappOptInCheckbox";

export function AgenticCartLayout({
  bundle,
  contact,
  shipping,
  status,
  visibleError,
  canPay,
  onContactChange,
  onShippingChange,
  onPay,
  recovery,
  resumed,
  serviceability,
  whatsappConsent = true,
  onWhatsappConsentChange,
  birthPrefilled,
}: {
  bundle: AgenticCartBundleSummaryResponse | null;
  contact: ContactForm;
  shipping: ShippingForm;
  status: CartStatus;
  visibleError: string;
  canPay: boolean;
  onContactChange: (field: keyof ContactForm, value: string) => void;
  onShippingChange: (field: keyof ShippingForm, value: string) => void;
  onPay: () => Promise<void>;
  recovery?: CartRecoveryState | null;
  resumed?: boolean;
  serviceability?: ServiceabilityInfo | null;
  whatsappConsent?: boolean;
  onWhatsappConsentChange?: (value: boolean) => void;
  birthPrefilled?: boolean;
}) {
  return (
    <div role="main" className="min-h-screen bg-background px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto grid max-w-6xl gap-6 lg:grid-cols-[1.15fr_0.85fr]">
        <div className="space-y-4">
          {resumed && !recovery && <ResumedNotice />}
          {recovery && <RecoveryNotice recovery={recovery} />}
          <CheckoutFormPanel
            bundle={bundle}
            contact={contact}
            shipping={shipping}
            status={status}
            visibleError={visibleError}
            canPay={canPay}
            onContactChange={onContactChange}
            onShippingChange={onShippingChange}
            onPay={onPay}
            serviceability={serviceability}
            whatsappConsent={whatsappConsent}
            onWhatsappConsentChange={onWhatsappConsentChange}
            birthPrefilled={birthPrefilled}
          />
        </div>
        <PlanSummary bundle={bundle} />
      </div>
    </div>
  );
}

function ResumedNotice() {
  return (
    <div className="rounded-lg border border-ish-accent/30 bg-ish-accent-light px-4 py-3 text-sm text-ish-muted">
      Welcome back — your upay plan is saved. Your details are filled in; finish below to continue.
    </div>
  );
}

function RecoveryNotice({ recovery }: { recovery: CartRecoveryState }) {
  return (
    <div className="rounded-lg border border-ish-accent/30 bg-ish-accent-light px-4 py-3 text-sm text-ish-muted">
      <p className="font-semibold text-ish-dark">Your {recovery.bundleName} is saved.</p>
      <p className="mt-1">Finish whenever you are ready — your details are kept on this page.</p>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
        <a href={recovery.resumeHref} className="inline font-semibold text-intent-brand underline">
          Resume checkout
        </a>
        <a href={recovery.supportHref} className="inline font-semibold text-intent-brand underline">
          Get help
        </a>
      </div>
    </div>
  );
}

function CheckoutFormPanel({
  bundle,
  contact,
  shipping,
  status,
  visibleError,
  canPay,
  onContactChange,
  onShippingChange,
  onPay,
  serviceability,
  whatsappConsent = true,
  onWhatsappConsentChange,
  birthPrefilled,
}: {
  bundle: AgenticCartBundleSummaryResponse | null;
  contact: ContactForm;
  shipping: ShippingForm;
  status: CartStatus;
  visibleError: string;
  canPay: boolean;
  onContactChange: (field: keyof ContactForm, value: string) => void;
  onShippingChange: (field: keyof ShippingForm, value: string) => void;
  onPay: () => Promise<void>;
  serviceability?: ServiceabilityInfo | null;
  whatsappConsent?: boolean;
  onWhatsappConsentChange?: (value: boolean) => void;
  birthPrefilled?: boolean;
}) {
  return (
    <div className="rounded-lg border border-ish-border bg-ish-bg-card p-5 shadow-sm sm:p-6">
      <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.12em] text-intent-brand">
        Ishvaram Checkout
      </p>
      <p
        role="heading"
        aria-level={1}
        className="font-serif text-2xl font-bold text-ish-dark sm:text-3xl"
      >
        {bundle?.name ?? "Loading your upay plan"}
      </p>
      {bundle && <p className="mt-3 text-sm leading-6 text-ish-muted">{bundle.explanation}</p>}

      <ReportContactFields
        contact={contact}
        onContactChange={onContactChange}
        whatsappConsent={whatsappConsent}
        onWhatsappConsentChange={onWhatsappConsentChange}
        birthPrefilled={birthPrefilled}
      />

      {bundle?.requires_shipping && (
        <ShippingFields shipping={shipping} onShippingChange={onShippingChange} />
      )}
      {bundle?.requires_shipping && serviceability?.known && (
        <ServiceabilityHint info={serviceability} />
      )}

      {visibleError && (
        <p className="mt-4 rounded-md border border-error/20 bg-error/10 px-3 py-2 text-sm text-error">
          {visibleError}
        </p>
      )}

      <button
        type="button"
        disabled={!canPay}
        onClick={() => void onPay()}
        className="btn-sacred mt-6 w-full rounded-lg px-5 py-3 text-sm font-bold sm:w-auto"
      >
        {status === "paying" ? "Opening payment" : bundle?.primary_cta_label || "Continue"}
      </button>

      <TrustStrip />
    </div>
  );
}

function TrustStrip() {
  return (
    <div className="mt-4 space-y-1 text-xs leading-5 text-ish-muted">
      <p className="flex items-center gap-1.5 font-semibold text-ish-dark">
        <LockIcon />
        Secure payment via Razorpay · UPI &amp; cards
      </p>
      <p>Your payment and delivery details stay with Ishvaram and its fulfilment providers.</p>
    </div>
  );
}

function LockIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="4.5" y="11" width="15" height="9.5" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}

function ReportContactFields({
  contact,
  onContactChange,
  whatsappConsent = true,
  onWhatsappConsentChange,
  birthPrefilled,
}: {
  contact: ContactForm;
  onContactChange: (field: keyof ContactForm, value: string) => void;
  whatsappConsent?: boolean;
  onWhatsappConsentChange?: (value: boolean) => void;
  birthPrefilled?: boolean;
}) {
  const [editingBirth, setEditingBirth] = useState(false);
  // When the seeker already gave their birth details in chat, collapse them into
  // a one-line summary (with Edit) so they don't re-read a 4-field form — just
  // confirm + add contact. No prefill → the full birth form, exactly as before.
  const showBirthForm = !birthPrefilled || editingBirth;
  return (
    <div className="mt-6 space-y-4">
      {showBirthForm ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            label="Name for report"
            value={contact.name}
            onChange={(value) => onContactChange("name", value)}
            autoComplete="name"
          />
          <TextField
            label="Birth place"
            value={contact.birthPlace}
            onChange={(value) => onContactChange("birthPlace", value)}
            autoComplete="address-level2"
          />
          <TextField
            label="Birth date"
            type="date"
            value={contact.birthDate}
            onChange={(value) => onContactChange("birthDate", value)}
          />
          <TextField
            label="Birth time"
            type="time"
            value={contact.birthTime}
            onChange={(value) => onContactChange("birthTime", value)}
          />
        </div>
      ) : (
        <BirthSummary contact={contact} onEdit={() => setEditingBirth(true)} />
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          label="Email"
          type="email"
          value={contact.email}
          onChange={(value) => onContactChange("email", value)}
          autoComplete="email"
        />
        <TextField
          label="WhatsApp"
          type="tel"
          value={contact.phone}
          onChange={(value) => onContactChange("phone", value)}
          autoComplete="tel"
        />
      </div>
      {/* WhatsApp order/delivery opt-in — only when a phone is entered. Pre-checked;
          threads into recovery_phone_consent on the abandon payload. */}
      {contact.phone.trim() && onWhatsappConsentChange ? (
        <WhatsappOptInCheckbox
          checked={whatsappConsent}
          onChange={onWhatsappConsentChange}
          language="en"
        />
      ) : null}
    </div>
  );
}

function BirthSummary({ contact, onEdit }: { contact: ContactForm; onEdit: () => void }) {
  const birthDate = formatBirthDate(contact.birthDate);
  return (
    <div className="flex items-center justify-between gap-3 rounded-md border border-ish-border-light bg-ish-accent-light px-3 py-2.5 text-sm">
      <span className="text-ish-muted">
        Report for <span className="font-semibold text-ish-dark">{contact.name}</span>
        {birthDate ? ` · born ${birthDate}` : ""}
        {contact.birthPlace ? ` · ${contact.birthPlace}` : ""}
      </span>
      <button
        type="button"
        onClick={onEdit}
        className="shrink-0 font-semibold text-intent-brand underline"
      >
        Edit
      </button>
    </div>
  );
}

function formatBirthDate(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return iso;
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = months[Number(match[2]) - 1] ?? match[2];
  return `${Number(match[3])} ${month} ${match[1]}`;
}

function ServiceabilityHint({ info }: { info: ServiceabilityInfo }) {
  if (!info.serviceable) {
    return (
      <p className="mt-2 text-sm text-ish-muted">
        We do not currently ship to PIN {info.pincode}. Please try another delivery pincode.
      </p>
    );
  }
  const eta =
    info.estimated_days != null
      ? `Delivers in about ${info.estimated_days} day${info.estimated_days === 1 ? "" : "s"}`
      : "Deliverable to your pincode";
  return (
    <p className="mt-2 text-sm font-medium text-intent-brand">
      {eta}
      {info.cod_available ? " - Cash on Delivery available" : ""}
    </p>
  );
}

function ShippingFields({
  shipping,
  onShippingChange,
}: {
  shipping: ShippingForm;
  onShippingChange: (field: keyof ShippingForm, value: string) => void;
}) {
  return (
    <div className="mt-6 border-t border-ish-border-light pt-5">
      <p role="heading" aria-level={2} className="font-serif text-base font-bold text-ish-dark">
        Delivery address
      </p>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <TextField
          label="First name"
          value={shipping.first_name}
          onChange={(value) => onShippingChange("first_name", value)}
          autoComplete="given-name"
        />
        <TextField
          label="Last name"
          value={shipping.last_name ?? ""}
          onChange={(value) => onShippingChange("last_name", value)}
          autoComplete="family-name"
        />
        <TextField
          label="Address"
          value={shipping.address1}
          onChange={(value) => onShippingChange("address1", value)}
          autoComplete="address-line1"
        />
        <TextField
          label="Apartment, area"
          value={shipping.address2 ?? ""}
          onChange={(value) => onShippingChange("address2", value)}
          autoComplete="address-line2"
        />
        <TextField
          label="City"
          value={shipping.city}
          onChange={(value) => onShippingChange("city", value)}
          autoComplete="address-level2"
        />
        <TextField
          label="State"
          value={shipping.province ?? ""}
          onChange={(value) => onShippingChange("province", value)}
          autoComplete="address-level1"
        />
        <TextField
          label="PIN code"
          value={shipping.zip}
          onChange={(value) => onShippingChange("zip", value)}
          autoComplete="postal-code"
        />
        <TextField
          label="Delivery phone"
          type="tel"
          value={shipping.phone}
          onChange={(value) => onShippingChange("phone", value)}
          autoComplete="tel"
        />
      </div>
    </div>
  );
}

function TextField({
  label,
  value,
  onChange,
  type = "text",
  autoComplete,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  autoComplete?: string;
}) {
  return (
    <label className="block text-sm font-semibold text-ish-dark">
      <span>{label}</span>
      <input
        className="input-sacred mt-1 w-full rounded-md px-3 py-2 text-sm text-ish-text"
        value={value}
        type={type}
        autoComplete={autoComplete}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}
